'use client';

import { useMemo, type CSSProperties } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale, useWikiSource, type WikiTreeNode } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { currentWikiSlug, findWikiNode } from './wikiSlug';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Page header for a wiki doc — title ({@code h1}), an optional lead
 * (excerpt) and a "Last updated {date}" line. Sits above the bound
 * {@code EntityContent} body in the {@code wiki-detail} layout so the
 * page reads like a documentation article rather than a bare HTML dump.
 *
 * <p>Reads everything from the pre-computed tree node (title / excerpt /
 * updatedAt are already on {@code WikiTreeNode}), so no extra fetch
 * beyond the tree every wiki block already loads.
 */
export interface WikiPageMetaProps {
  /** Show the "Last updated" line. Default true. */
  showLastUpdated?: boolean;
  /** Show the excerpt lead paragraph. Default true. */
  showExcerpt?: boolean;
  /** Label before the date. Default "Last updated". */
  updatedLabel?: string;
}

export function WikiPageMeta({
  showLastUpdated = true,
  showExcerpt = true,
  updatedLabel = 'Last updated',
}: WikiPageMetaProps) {
  const wiki = useWikiSource();
  const { locale } = useLocale();
  const pathname = usePathname() ?? '';
  const slug = useMemo(() => currentWikiSlug(pathname), [pathname]);

  const treeState = useAsyncResource<WikiTreeNode[]>(async () => {
    if (!wiki) return null;
    const tree = await wiki.getTree();
    return tree.length > 0 ? tree : null;
  }, [wiki]);

  const node = useMemo(() => {
    if (treeState.kind !== 'loaded' || !slug) return null;
    return findWikiNode(treeState.data, slug);
  }, [treeState, slug]);

  if (!node) return null;

  const updated = showLastUpdated ? formatDate(node.updatedAt, locale) : '';

  return (
    <BlockRoot as="header" style={headerStyle}>
      <h1 style={titleStyle}>{node.title}</h1>
      {showExcerpt && node.excerpt ? <p style={leadStyle}>{node.excerpt}</p> : null}
      {updated ? (
        <p style={metaStyle}>
          {updatedLabel} <time dateTime={node.updatedAt}>{updated}</time>
        </p>
      ) : null}
    </BlockRoot>
  );
}

function formatDate(iso: string, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

const headerStyle: CSSProperties = {
  margin: '0 0 var(--space-6, 24px)',
  paddingBottom: 'var(--space-4, 16px)',
  borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-3xl, 32px)',
  lineHeight: 1.2,
  fontWeight: 700,
  color: 'var(--color-text-primary, #18181b)',
};

const leadStyle: CSSProperties = {
  margin: 'var(--space-3, 12px) 0 0',
  fontSize: 'var(--font-size-lg, 18px)',
  lineHeight: 1.5,
  color: 'var(--color-text-secondary, #52525b)',
};

const metaStyle: CSSProperties = {
  margin: 'var(--space-3, 12px) 0 0',
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-tertiary, #71717a)',
};
